import * as React from 'react';
import styled, {css} from 'styled-components';
import {
  Container as LastTransactionsContainer,
} from './LastTransactions';
import {transparentize} from 'polished';
import {IconArrowBracket} from 'icons';
import {transition} from 'helpers/css';
import {Theme} from '../../../stores/AppStore';

export enum ItemCardType {
  Course='course',
  Webinar='webinar',
}

export interface ItemCardProps {
  type: ItemCardType;
  title?: string;
  info?: string;
  imgSrc?: string;
  onBtnClick?: () => void;
}

const Info = styled.div`
  width: max-content;
  border-radius: 5px;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 16px;
  color: #ffffff;
  background-color: ${({theme}) => transparentize(.85, theme.variables.color)};
`;

const Container = styled(LastTransactionsContainer)<
  Pick<ItemCardProps, 'type'>
>`
  padding: 12px;
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 10px;

  ${Info} {
    ${({type, theme}) => {
    switch (type) {
      case ItemCardType.Course: return css`
        color: ${theme.name === Theme.Dark ? '#ffffff' : '#000000'};
        background-color: ${transparentize(.85, theme.variables.color)};
      `;
      case ItemCardType.Webinar: return css`
        background-color: ${theme.colors.primary};
     `;
    }
  }}
  }
  
`;

export const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content;
  gap: 20px;
  padding: 20px;
`;

const PreviewBox = styled.div`
  width: 164px;
  height: 92px;
  overflow: hidden;
  border-radius: 5px;
  background-color: #232323;
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;


const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin: 8px 0;
`;

const Button = styled.button`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 6px;
  align-items: center;
  width: max-content;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${({theme}) => theme.colors.primaryLight};
  
  ${transition()}
  
  &:hover {
    color: ${({theme}) => theme.colors.blueViolet};
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/**
 * The function render item card
 * @param {ItemCardProps} props
 * @constructor
 */
export default function ItemCard(props: ItemCardProps) {
  const notFoundImage = `${process.env.PUBLIC_URL}/images/not-found.png`;

  const [
    imgSrc, setImgSrc,
  ] = React.useState<string>(props.imgSrc || notFoundImage);

  /**
   * The function render preview
   * @return {JSX.Element}
   */
  function renderPreview() {
    const image = imgSrc && (
      <Image
        src={imgSrc}
        onError={() => (
          setImgSrc(notFoundImage)
        )}
      />
    );

    return (
      <PreviewBox>
        {image}
      </PreviewBox>
    );
  }

  /**
   * The function render info
   * @return {JSX.Element | undefined}
   */
  function renderInfo() {
    if (props.info) {
      return <Info>{props.info}</Info>;
    }
  }

  /**
   * The function render title
   * @return {JSX.Element}
   */
  function renderTitle() {
    return <ItemTitle>{props.title}</ItemTitle>;
  }

  /**
   * The function render link button
   * @return {JSX.Element}
   */
  function renderButton(): JSX.Element {
    let label = '';

    switch (props.type) {
      case ItemCardType.Course:
        label = 'Открыть';
        break;
      case ItemCardType.Webinar:
        label = 'Войти';
        break;
    }

    return (
      <Button onClick={props.onBtnClick}>
        {label}
        {' '}
        <IconArrowBracket />
      </Button>
    );
  }

  return (
    <Container type={props.type}>
      {renderPreview()}
      <ItemContent>
        {renderInfo()}
        {renderTitle()}
        {renderButton()}
      </ItemContent>
    </Container>
  );
}
